import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getSiteBrandName } from '../Utils/brand';
import { getAuthConfig } from '../Utils/authConfig';

export default function Footer() {
    const brandName = getSiteBrandName();
    const [animKey, setAnimKey] = useState(0);
    const [authEnabled, setAuthEnabled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const telegramBotUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';
    const telegramContactLink = process.env.REACT_APP_TELEGRAM_CONTACT || (telegramBotUsername ? `https://t.me/${telegramBotUsername}` : 'https://t.me');
    const telegramChannelUsername = process.env.REACT_APP_TELEGRAM_CHANNEL_USERNAME || process.env.TELEGRAM_CHANNEL_USERNAME || '';
    const telegramChannelLink = process.env.REACT_APP_TELEGRAM_CHANNEL_URL || (telegramChannelUsername ? (telegramChannelUsername.startsWith('http') ? telegramChannelUsername : `https://t.me/${telegramChannelUsername.replace(/^@/, '')}`) : 'https://t.me');

    useEffect(() => {
        getAuthConfig()
            .then(data => {
                if (data && (data.authEnabled !== undefined || data.success)) {
                    setAuthEnabled(Boolean(data.authEnabled));
                }
            })
            .catch(() => {});
    }, []);

    const hasSession = typeof window !== 'undefined' && Boolean(
        localStorage.getItem('token') || 
        localStorage.getItem('adminToken') || 
        localStorage.getItem('telegram_user')
    );

    return (
        <>
            <div className="divider" />
            <footer className="relative py-16 px-6 md:px-8 lg:px-16 border-t border-white/[0.08] overflow-hidden">
                {/* Background matching user menu dark theme with subtle ambient glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#090a0f]/90 via-[#07070a]/95 to-[#050507] pointer-events-none" />
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-32 bg-primary-500/[0.04] rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                    {/* Brand Section */}
                    <div className="space-y-6">
                        <div className="space-y-4">
                            {/* Logo */}
                            <div className="flex items-center space-x-3">
                                <h2 
                                    onClick={() => {
                                        setAnimKey(prev => prev + 1);
                                        if (location.pathname !== '/') {
                                            navigate('/');
                                            setTimeout(() => {
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }, 50);
                                        } else {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }
                                    }}
                                    className="text-xl font-display font-bold text-white flex items-center gap-2 cursor-pointer select-none group"
                                >
                                    <motion.span
                                        key={`footer-text-${animKey}`}
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.35, ease: "easeOut" }}
                                        whileHover={{ 
                                            scale: 1.04,
                                            color: "#93c5fd",
                                            textShadow: "0 0 16px rgba(59, 130, 246, 0.5)"
                                        }}
                                        className="text-white transition-colors duration-200 tracking-tight inline-block"
                                    >
                                        About
                                    </motion.span>
                                    <motion.span 
                                        key={`footer-tg-badge-${animKey}`}
                                        initial={{ x: -28, rotate: -180, scale: 0.2, opacity: 0 }}
                                        animate={{ 
                                            x: 0, 
                                            rotate: 0, 
                                            scale: 1, 
                                            opacity: 1,
                                            transition: {
                                                type: "spring",
                                                stiffness: 280,
                                                damping: 18,
                                                mass: 0.8,
                                                delay: 0.35
                                            }
                                        }}
                                        whileHover={{ 
                                            scale: 1.12, 
                                            rotate: [0, -6, 6, 0],
                                            transition: { duration: 0.3 }
                                        }}
                                        whileTap={{ scale: 0.92 }}
                                        className="w-12 h-9 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 inline-flex items-center justify-center text-white font-bold text-lg shadow-md shadow-primary-500/25 hover:shadow-primary-500/40 transition-shadow"
                                    >
                                        Buy
                                    </motion.span>
                                </h2>
                            </div>
                            
                            <p className="text-neutral-400 text-base leading-relaxed max-w-md">
                                Premium web names marketplace. Buy, rent, and forward with instant setup.
                            </p>
                        </div>
                    </div>

                    {/* Links Section */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Marketplace</h3>
                            <ul className="space-y-2.5 text-neutral-400 text-sm">
                                <li><a href="/#domains" className="hover:text-white transition-colors">Buy Domains</a></li>
                                <li><a href="/#domains" className="hover:text-white transition-colors">Rent Domains</a></li>
                                <li><a href="/#domains" className="hover:text-white transition-colors">Forward Domains</a></li>
                            </ul>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Resources</h3>
                            <ul className="space-y-2.5 text-neutral-400 text-sm">
                                <li><Link to="/whois" className="hover:text-white transition-colors">WHOIS Lookup</Link></li>
                                <li><Link to="/faq.html" className="hover:text-white transition-colors">Help Center & FAQ</Link></li>
                                <li><Link to="/contact.html" className="hover:text-white transition-colors">Contact Support</Link></li>
                                {telegramContactLink && (
                                    <li>
                                        <a href={telegramContactLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                                            <span>Telegram Bot</span>
                                            <span className="text-xs">↗</span>
                                        </a>
                                    </li>
                                )}
                                {telegramChannelLink && (
                                    <li>
                                        <a href={telegramChannelLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                                            <span>Channel Updates</span>
                                            <span className="text-xs">↗</span>
                                        </a>
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Legal</h3>
                            <ul className="space-y-2.5 text-neutral-400 text-sm">
                                <li><Link to="/terms.html" className="hover:text-white transition-colors">Terms of Service</Link></li>
                                <li><Link to="/privacy.html" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                                {(authEnabled || hasSession) && (
                                    <li><Link to="/cabinet" className="hover:text-white transition-colors">Cabinet</Link></li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-14 pt-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex flex-wrap items-center gap-3">
                            {telegramChannelLink && (
                                <a
                                    href={telegramChannelLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30 text-xs text-primary-300 hover:text-white transition-all shadow-[0_0_15px_rgba(14,165,233,0.15)]"
                                >
                                    <svg className="w-4 h-4 text-primary-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.52 2.77-1.16 3.35-1.36 3.73-1.37.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                                    </svg>
                                    <span className="font-medium">Channel Updates</span>
                                </a>
                            )}
                        </div>
                        
                        <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-neutral-400">
                            <Link to="/terms.html" className="hover:text-white transition-colors">Terms of Service</Link>
                            <Link to="/privacy.html" className="hover:text-white transition-colors">Privacy Policy</Link>
                            <Link to="/faq.html" className="hover:text-white transition-colors">FAQ</Link>
                            <Link to="/contact.html" className="hover:text-white transition-colors">Contact</Link>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <Copyright brandName={brandName} />
            </div>
        </footer>
        </>
    )
}
const Copyright = ({ brandName }) => (
    <div className="mt-8 pt-6 border-t border-white/[0.08] text-center text-neutral-500 text-xs">
        <p>
            © {new Date().getFullYear()} {brandName || 'Domainer'}
        </p>
    </div>
);
